"use client";

import { useEffect, useContext, useState, useMemo } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  TextField,
  Chip,
  Button,
  Autocomplete,
  IconButton,
  Card,
  CardContent,
  Divider,
  alpha,
  Tooltip,
  Badge,
  Tabs,
  Tab,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { DataGrid } from "@mui/x-data-grid";
import { ErpContext } from "../../context/ErpContext.js";
import ModalDetalleCuentaCorriente from "./graficas/modal-detalle-cuenta-corriente";
import CustomPagination from "./CustomPagination";
import ModalMedioDePago from "./ModalMedioDePago.js";
import { getLocaleConfig } from "../../utils/numeros";

// Icons
import SearchIcon from "@mui/icons-material/Search";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import BalanceIcon from "@mui/icons-material/Balance";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import ReceiptIcon from "@mui/icons-material/Receipt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import PaymentsIcon from '@mui/icons-material/Payments';
import InfoIcon from "@mui/icons-material/Info";

import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import RefreshIcon from "@mui/icons-material/Refresh";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";

// Definir el tema personalizado
const theme = createTheme({
  palette: {
    mode: "light",
    primary: {
      main: "#5b5b5f",
    },
    secondary: {
      main: "#f4b10e", // Color amarillo/naranja
    },
    success: {
      main: "#4CAF50",
    },
    error: {
      main: "#F44336",
    },
    warning: {
      main: "#FF8042",
    },
    info: {
      main: "#0088FE",
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h6: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          overflow: "visible",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: "none",
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: "#5b5b5f",
            color: "white",
            borderBottom: "none",
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: 600,
          },
          "& .MuiDataGrid-cell": {
            borderBottom: "1px solid #f0f0f0",
          },
          "& .MuiDataGrid-row": {
            "&:nth-of-type(even)": {
              backgroundColor: "#f9f9f9",
            },
            "&:hover": {
              backgroundColor: "#f0f4fa",
            },
          },
        },
      },
    },
  },
});

// Componente principal
const CuentasCorrientesTable = () => {
  const {
    cuentaCorrienteTransformed,
    getCuentaCorrienteTransformed,
    isNavbarCollapsed,
    user,
    postTransaccionPago,
    postCrearPago,
    postTransaccion,
    websocketSyncService,
    formatoNumericoGlobal,
  } = useContext(ErpContext);

  // Configuración de locale (memoizado para evitar recálculos)
  const localeConfig = useMemo(
    () => getLocaleConfig(formatoNumericoGlobal || null),
    [formatoNumericoGlobal]
  );

  // Estados principales
  const [openModal, setOpenModal] = useState(false);
  const [selectedCuentaCorriente, setSelectedCuentaCorriente] = useState(null);
  const [page, setPage] = useState(1);
  const limit = 100;
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [montoTotalTransacciones, setMontoTotalTransacciones] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cuentasCorrientesTransacciones, setCuentasCorrientesTransacciones] =
    useState([]);

  const [abrirMedioDePago, setAbrirMedioDePago] = useState(false);
  const [precioTotal, setPrecioTotal] = useState(0);

  // Estados para filtros
  const [filteredData, setFilteredData] = useState([]);
  const [searchEntidadText, setSearchEntidadText] = useState("");
  const [searchDniText, setSearchDniText] = useState("");
  const [searchPagosText, setSearchPagosText] = useState("");
  const [tipoEntidadFilter, setTipoEntidadFilter] = useState(null);
  const [tiposEntidad, setTiposEntidad] = useState([]);

  // Estadísticas calculadas
  const [stats, setStats] = useState({
    saldoACobrar: 0, // Suma de saldoCalculado positivos de clientes
    cantidadClientesDeudores: 0, // Cantidad de clientes con saldoCalculado > 0
    saldoAFavor: 0, // Suma de saldoCalculado positivos de proveedores
    cantidadProveedoresCredito: 0, // Cantidad de proveedores con saldoCalculado > 0
    totalDeudas: 0, // Suma de saldo legacy (saldo) de proveedores con saldo > 0 (Cuentas por Pagar)
    cantidadDeudas: 0, // Cantidad de proveedores con saldo legacy > 0
  });

  // Estado para tab activo: 'adelantos' (sistema nuevo) o 'deudas' (legacy)
  const [tabActivo, setTabActivo] = useState('adelantos');

  // Estado para filas actualizadas vía WebSocket (para indicador visual)
  const [filasActualizadas, setFilasActualizadas] = useState(new Set());

  // Obtener datos al cargar el componente
  useEffect(() => {
    fetchData();
  }, []);

  // Suscribirse a actualizaciones de cuenta corriente vía WebSocket
  useEffect(() => {
    if (!websocketSyncService) return;

    console.log('📡 Suscribiéndose a actualizaciones de cuenta corriente...');

    const unsubscribe = websocketSyncService.subscribe('cuentaCorriente:updated', (data) => {
      console.log('🔔 Actualización de cuenta corriente recibida:', data);

      const { idEntidad, saldo, monto, cobrado, saldoCalculado } = data.data || {};

      if (!idEntidad) {
        console.warn('⚠️ Actualización sin idEntidad, ignorando');
        return;
      }

      // Actualizar la fila específica en la tabla
      setCuentasCorrientesTransacciones(prev => 
        prev.map(fila => {
          if (fila.idEntidad === idEntidad) {
            console.log(`✅ Actualizando fila de entidad ${idEntidad}`);
            
            // Marcar como actualizada para mostrar indicador visual
            setFilasActualizadas(prevSet => {
              const newSet = new Set(prevSet);
              newSet.add(idEntidad);
              return newSet;
            });

            // Remover indicador después de 5 segundos
            setTimeout(() => {
              setFilasActualizadas(prevSet => {
                const newSet = new Set(prevSet);
                newSet.delete(idEntidad);
                return newSet;
              });
            }, 5000);

            // Retornar fila actualizada con nuevos valores
            return {
              ...fila,
              saldo: saldo !== undefined ? saldo : fila.saldo,
              monto: monto !== undefined ? monto : fila.monto,
              cobrado: cobrado !== undefined ? cobrado : fila.cobrado,
              saldoCalculado: saldoCalculado !== undefined ? saldoCalculado : fila.saldoCalculado,
            };
          }
          return fila;
        })
      );
    });

    // Cleanup: desuscribirse al desmontar
    return () => {
      console.log('📡 Desuscribiéndose de actualizaciones de cuenta corriente');
      unsubscribe();
    };
  }, [websocketSyncService]);

  // ✅ Recargar datos cuando cambia el tab activo
  useEffect(() => {
    console.log('🔄 Tab activo cambió a:', tabActivo);
    fetchData(1, limit);
  }, [tabActivo]);

  const fetchData = async (
    pageParam = 1,
    limitParam = limit
  ) => {
    try {
      setLoading(true);
      const controller = new AbortController();
      const signal = controller.signal;

      const response = await getCuentaCorrienteTransformed(
        1,
        pageParam,
        limitParam,
        { signal },
        tabActivo, // ✅ Pasar tab activo al backend
      );

      // Verificar si la respuesta tiene la estructura esperada de paginación
      let cuentas, paginationData;
      if (response && typeof response === "object" && response.data) {
        // Respuesta con paginación
        cuentas = response.data;
        paginationData = {
          currentPage: response.currentPage || pageParam,
          totalPages: response.totalPages || 1,
          total: response.total || 0,
          totalMonto: response.totalMonto || 0,
        };
      } else {
        // Respuesta directa (array)
        cuentas = Array.isArray(response) ? response : [];
        paginationData = {
          currentPage: pageParam,
          totalPages: 1,
          total: cuentas.length,
          totalMonto: 0,
        };
      }

      // Me aseguro de tener siempre un array
      const list = Array.isArray(cuentas) ? cuentas : [];

      // Transformo para el DataGrid
      const cuentasTransformadas = await Promise.all(
        list.map(async (cc) => {
          const entidad = cc.entidad || {};
          const tipoEntDesc = entidad.tipoEntidad?.descripcion || "";

          // Garantizo siempre un array de transacciones
          const rawTxs = Array.isArray(cc.transacciones)
            ? cc.transacciones
            : [];
          const txsItems = Array.isArray(cc.transaccionesItems)
            ? cc.transaccionesItems
            : [];

          // Extraer todos los items de todas las transacciones
          const allItems = [];
          const itemsDescriptions = [];

          txsItems.forEach((tx) => {
            if (Array.isArray(tx.TransaccionItems)) {
              tx.TransaccionItems.forEach((item) => {
                if (item.item) {
                  allItems.push({
                    id: item.idItem,
                    descripcion: item.item.descripcion || "Sin descripción",
                    cantidad: item.cantidad || 0,
                    precio: item.precio || 0,
                    montoTotal: item.montoTotal || 0,
                    fecha: tx.fechaHoraCreacion,
                    idTransaccion: tx.id,
                  });
                  itemsDescriptions.push(
                    item.item.descripcion || "Sin descripción"
                  );
                }
              });
            }
          });

          // Eliminar duplicados de descripciones
          const uniqueItemsDescriptions = [...new Set(itemsDescriptions)];

          const txs = rawTxs.sort(
            (a, b) =>
              new Date(b.fechaHoraCreacion).getTime() -
              new Date(a.fechaHoraCreacion).getTime()
          );
          const lastTx = txs[0] || {};
          const lastDateFormatted = lastTx.fechaHoraCreacion
            ? new Intl.DateTimeFormat("es-AR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }).format(new Date(lastTx.fechaHoraCreacion))
            : "";
          const fechaCreacionCC = cc.updatedAt
            ? new Intl.DateTimeFormat("es-AR", {
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
              }).format(new Date(cc.updatedAt))
            : "";

          // Campos calculados dinámicamente desde el backend
          const monto = cc.monto || 0; // Total servicios contratados (proveedores) o precio paquetes (clientes)
          const cobrado = cc.cobrado || 0; // Adelantos dados (proveedores) o cuotas pagadas (clientes)
          const saldoCalculado = cc.saldoCalculado || 0; // monto - cobrado

          return {
            id: cc.idEntidad, // para key / getRowId
            idEntidad: cc.idEntidad,
            tipoEntidad: tipoEntDesc,
            descripcion: entidad.descripcion || "",
            apellido: entidad.apellido || "",
            dniCuitCuil: entidad.dniCuitCuil || "",
            telefono: entidad.telefono || "",
            email: entidad.email || "",
            saldo: cc.saldo, // Saldo real de CC (se mantiene sin cambios)
            fechaCreacionCC: fechaCreacionCC || "",

            // Información de items
            items: allItems,
            itemsCount: allItems.length,
            itemsDescriptions: uniqueItemsDescriptions,
            itemsDescriptionsText: uniqueItemsDescriptions.join(", "),

            // Resumen transacciones
            transCount: txs.length,
            lastTransDate: lastDateFormatted,
            lastTransAmount: lastTx.montoTotal || 0,
            
            // Nuevos campos calculados del backend
            monto, // Total comprometido (servicios o paquetes)
            cobrado, // Total pagado/cobrado (adelantos o cuotas)
            saldoCalculado, // monto - cobrado

            // Detalle completo
            transacciones: txs,
            transaccionesItems: txsItems,
            entidadCompleta: entidad,
            cuentaCorrienteCompleta: cc,
          };
        })
      );

      // Actualizar estados de paginación
      setPage(paginationData.currentPage);
      setTotalPages(paginationData.totalPages);
      setTotalCount(paginationData.total);
      setMontoTotalTransacciones(paginationData.totalMonto);

      // Extraer tipos de entidad únicos
      const tiposEntidadUnicos = [
        ...new Set(
          cuentasTransformadas
            .map((cuenta) => cuenta.tipoEntidad)
            .filter(Boolean)
        ),
      ];
      setTiposEntidad(
        tiposEntidadUnicos.map((tipo) => ({ label: tipo, value: tipo }))
      );

      // Calcular estadísticas basadas en saldoCalculado
      // Filtrar clientes con saldoCalculado > 0 (deben dinero por paquetes)
      const clientesDeudores = cuentasTransformadas.filter(
        (row) => row.tipoEntidad === "Cliente" && (row.saldoCalculado || 0) > 0
      );
      
      // Filtrar proveedores con saldoCalculado > 0 (tienen crédito disponible)
      const proveedoresCredito = cuentasTransformadas.filter(
        (row) => row.tipoEntidad === "Proveedor" && (row.saldoCalculado || 0) > 0
      );

      // Sumar saldos calculados
      const saldoACobrar = clientesDeudores.reduce(
        (acc, row) => acc + (row.saldoCalculado || 0),
        0
      );
      
      const saldoAFavor = proveedoresCredito.reduce(
        (acc, row) => acc + (row.saldoCalculado || 0),
        0
      );

      // Calcular "Cuentas por Pagar" (saldos legacy de proveedores con saldo > 0)
      const proveedoresConDeuda = cuentasTransformadas.filter(
        (row) => row.tipoEntidad === "Proveedor" && (row.saldo || 0) > 0
      );
      
      const totalDeudas = proveedoresConDeuda.reduce(
        (acc, row) => acc + (row.saldo || 0),
        0
      );

      setStats({
        saldoACobrar,
        cantidadClientesDeudores: clientesDeudores.length,
        saldoAFavor,
        cantidadProveedoresCredito: proveedoresCredito.length,
        totalDeudas,
        cantidadDeudas: proveedoresConDeuda.length,
      });

      // Seteo directo la lista transformada
      setCuentasCorrientesTransacciones(cuentasTransformadas);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  // Aplicar filtros cuando cambian
  useEffect(() => {
    if (cuentasCorrientesTransacciones.length > 0) {
      let filtered = [...cuentasCorrientesTransacciones];

      // Filtro por entidad
      if (searchEntidadText) {
        const searchLower = searchEntidadText.toLowerCase();
        filtered = filtered.filter((row) => {
          const nombre = `${row.descripcion || ""} ${row.apellido || ""}`.trim();
          return nombre.toLowerCase().includes(searchLower);
        });
      }

      // Filtro por DNI/CUIT/CUIL
      if (searchDniText) {
        const searchLower = searchDniText.toLowerCase();
        filtered = filtered.filter(
          (row) =>
            row.dniCuitCuil &&
            row.dniCuitCuil.toLowerCase().includes(searchLower)
        );
      }

      // Filtro por tipo de entidad (solo en Cuenta Corriente)
      if (tabActivo === "adelantos" && tipoEntidadFilter) {
        filtered = filtered.filter(
          (row) => row.tipoEntidad === tipoEntidadFilter.value
        );
      }

      // Filtro por # Pagos
      if (searchPagosText) {
        const pagosNumber = parseInt(searchPagosText, 10);
        if (!Number.isNaN(pagosNumber)) {
          filtered = filtered.filter(
            (row) => (row.transCount || 0) === pagosNumber
          );
        }
      }

      setFilteredData(filtered);

    }
  }, [
    cuentasCorrientesTransacciones,
    searchEntidadText,
    searchDniText,
    searchPagosText,
    tipoEntidadFilter,
    tabActivo,
  ]);

  // Actualizar estadísticas cuando cambian los datos filtrados
  useEffect(() => {
    if (filteredData.length > 0) {
      // Calcular estadísticas basadas en saldoCalculado de los datos filtrados
      // Filtrar clientes con saldoCalculado > 0 (deben dinero por paquetes)
      const clientesDeudores = filteredData.filter(
        (row) => row.tipoEntidad === "Cliente" && (row.saldoCalculado || 0) > 0
      );
      
      // Filtrar proveedores con saldoCalculado > 0 (tienen crédito disponible)
      const proveedoresCredito = filteredData.filter(
        (row) => row.tipoEntidad === "Proveedor" && (row.saldoCalculado || 0) > 0
      );

      // Sumar saldos calculados
      const saldoACobrar = clientesDeudores.reduce(
        (acc, row) => acc + (row.saldoCalculado || 0),
        0
      );
      
      const saldoAFavor = proveedoresCredito.reduce(
        (acc, row) => acc + (row.saldoCalculado || 0),
        0
      );

      // Calcular "Cuentas por Pagar" (saldos legacy de proveedores con saldo > 0)
      const proveedoresConDeuda = filteredData.filter(
        (row) => row.tipoEntidad === "Proveedor" && (row.saldo || 0) > 0
      );
      
      const totalDeudas = proveedoresConDeuda.reduce(
        (acc, row) => acc + (row.saldo || 0),
        0
      );

      setStats({
        saldoACobrar,
        cantidadClientesDeudores: clientesDeudores.length,
        saldoAFavor,
        cantidadProveedoresCredito: proveedoresCredito.length,
        totalDeudas,
        cantidadDeudas: proveedoresConDeuda.length,
      });
    }
  }, [filteredData]);

  // Funciones para manejar filtros
  const buttonFiltrado = () => {
    fetchData(1);
  };

  // Función para abrir el modal de detalles
  const handleOpenModal = (cuentaCorriente) => {
    setSelectedCuentaCorriente(cuentaCorriente);
    setOpenModal(true);
  };

  // Función para cerrar el modal de detalles
  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedCuentaCorriente(null);
  };

  // Función para cerrar el modal de gráficas
  const handleCloseMedioDePago = () => {
    setAbrirMedioDePago(false);
  };

  const handleOpenMediosDePago = (datos) => {
    console.log("handleOpenMediosDePago datos: ", datos);
    setPrecioTotal(Math.abs(datos.saldo)); // Usar valor absoluto del saldo
    setSelectedCuentaCorriente(datos); // Guardar los datos de la cuenta
    setAbrirMedioDePago(true);
  };

  const handleConfirmarPago = async (
    mediosDePago,
    montoTotalMediosPago,
    transaccionSeleccionada,
    descripcion
  ) => {
    // Calcular si es pago completo o parcial
    const esPagoCompleto =
      Math.abs(montoTotalMediosPago) >=
      Math.abs(transaccionSeleccionada?.saldo);

    const descripcionFinal = descripcion || "Pago cuenta corriente";

    console.log("user", user);
    console.log("transaccionSeleccionada", transaccionSeleccionada);
    console.log("esPagoCompleto", esPagoCompleto);

let transaccionResponse;

    if (transaccionSeleccionada.saldo <= 0) {
      // Cliente debe dinero al negocio, se crea un Ingreso
      const idTipoTransaccion = 4; // Ingreso
      
      transaccionResponse = await postTransaccion(
        montoTotalMediosPago, //Monto todal de la transaccion
        idTipoTransaccion,
        transaccionSeleccionada.id, //idEntidad
        null, // no es necesario enviar subcategoria
        descripcionFinal,
        user.id,
        1, // ubicación central
        1, // negocio central
        0, // descuento
        0, // porcentaje descuento
        null, // transaccionAsociada
        null, // fecha
        true, // afectaCuentaCorriente
        null, // clientOS
        true, // viaja
        false, // liberado
        null, // cuotas
        'operacionCaja' // ⭐ CLAVE: Indica que es un pago que salda deuda
      );

      console.log("transaccionSeleccionada: ", transaccionSeleccionada);
      console.log("montoTotalMediosPago: ", montoTotalMediosPago);
      console.log(transaccionResponse);

      const idTransaccion = transaccionResponse.data.id;

      for (const it of mediosDePago) {
        // Crear pago
        const transaccionCrearPago = await postCrearPago(
          1, //Moneda va por default 1 que es peso hardcoded
          1, // cotización
          it.id, //Medio de pago id
          it.monto
        );
        console.log("it.monto: ", it.monto);
        const idTransaccionCrearPago = transaccionCrearPago.data.id;
        await postTransaccionPago(idTransaccion, idTransaccionCrearPago);
      }
      
      // ✅ NOTA: postTransaccion ya actualiza CuentaCorriente.saldo en el backend
      // cuando afectaCuentaCorriente=true y operacionParaCuentaCorriente='operacionCaja'
      // NO llamar a updateCuentaCorriente aquí para evitar duplicar el efecto

    }

    else {
      // Negocio debe dinero al proveedor, se crea un Egreso
      const idTipoTransaccion = 5; // Egreso
      
      transaccionResponse = await postTransaccion(
        montoTotalMediosPago, //Monto todal de la transaccion
        idTipoTransaccion,
        transaccionSeleccionada.id, //idEntidad
        null, // no es necesario enviar subcategoria
        descripcionFinal,
        user.id,
        1, // ubicación central
        1, // negocio central
        0, // descuento
        0, // porcentaje descuento
        null, // transaccionAsociada
        null, // fecha
        true, // afectaCuentaCorriente
        null, // clientOS
        true, // viaja
        false, // liberado
        null, // cuotas
        'operacionCaja' // ⭐ CLAVE: Indica que es un pago que salda deuda
      );

      console.log("transaccionSeleccionada: ", transaccionSeleccionada);
      console.log("montoTotalMediosPago: ", montoTotalMediosPago);
      console.log(transaccionResponse);

      const idTransaccion = transaccionResponse.data.id;

      for (const it of mediosDePago) {
        // Crear pago
        const transaccionCrearPago = await postCrearPago(
          1, //Moneda va por default 1 que es peso hardcoded
          1, // cotización
          it.id, //Medio de pago id
          it.monto
        );
        console.log("it.monto: ", it.monto);
        const idTransaccionCrearPago = transaccionCrearPago.data.id;
        await postTransaccionPago(idTransaccion, idTransaccionCrearPago);
      }
      
      // ✅ NOTA: postTransaccion ya actualiza CuentaCorriente.saldo en el backend
      // cuando afectaCuentaCorriente=true y operacionParaCuentaCorriente='operacionCaja'
      // NO llamar a updateCuentaCorriente aquí para evitar duplicar el efecto

    }
   
    setAbrirMedioDePago(false);

    await fetchData(page);

    // Opcional: refrescar datos después del pago
    // fetchData()
  };

  const clearFilters = () => {
    setTipoEntidadFilter(null);
    setSearchEntidadText("");
    setSearchDniText("");
    setSearchPagosText("");
  };

  // Formatear moneda
  const formatCurrency = (value) => {
    return new Intl.NumberFormat("es-AR", {
      style: "currency",
      currency: "ARS",
    }).format(value);
  };

  // Formatear número entero con separadores de miles (para DNI/CUIT/CUIL)
  const formatIntegerNumber = (value) => {
    if (value == null || value === "") return "";
    
    // Remover caracteres no numéricos
    const numericValue = String(value).replace(/\D/g, "");
    if (numericValue === "") return "";
    
    const number = parseInt(numericValue, 10);
    if (Number.isNaN(number)) return "";
    
    // Formatear con separadores de miles según configuración regional
    const { locale } = localeConfig;
    
    try {
      // Usar Intl.NumberFormat sin decimales (solo separadores de miles)
      return new Intl.NumberFormat(locale, {
        useGrouping: true,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(number);
    } catch (error) {
      console.warn("⚠️ Error formateando número entero:", error);
      // Fallback: usar formato argentino
      return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    }
  };

  // Definición de columnas para la tabla
  const columns = [
    {
      field: "idEntidad",
      headerName: "ID",
      width: 70,
      flex: 0.3,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          ID
        </Typography>
      ),
    },
    {
      field: "fechaCreacionCC",
      headerName: "Fecha",
      width: 70,
      flex: 1,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Fecha
        </Typography>
      ),
    },
    {
      field: "tipoEntidad",
      headerName: "Tipo",
      width: 150,
      flex: 0.8,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Tipo Entidad
        </Typography>
      ),
      renderCell: (params) => {
        const val = params.value || "";
        return (
          <Chip
            icon={
              val.includes("Cliente") ? (
                <PersonIcon fontSize="small" />
              ) : (
                <BusinessIcon fontSize="small" />
              )
            }
            label={val}
            size="small"
            sx={{
              bgcolor: val.includes("Cliente")
                ? alpha(theme.palette.info.main, 0.1)
                : alpha(theme.palette.secondary.main, 0.1),
              color: val.includes("Cliente")
                ? theme.palette.info.main
                : theme.palette.secondary.main,
              fontWeight: "bold",
            }}
          />
        );
      },
    },
    {
      field: "descripcion",
      headerName: "Entidad",
      width: 180,
      flex: 1,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Entidad
        </Typography>
      ),
      renderCell: (params) => {
        const isUpdated = filasActualizadas.has(params.row.idEntidad);
        return (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {params.value || ""} {params.row.apellido || ""}
            </Typography>
            {isUpdated && (
              <Chip
              // icon refresh websocket
              icon={
                <RefreshIcon
                  sx={{
                    fontSize: 18,        // ← acá controlás el tamaño real
                  }}
                />
              }
                // label="●"
                // label="⟳" 
                size="small"
                color="success"
                sx={{
                  height: 20,
                  fontSize: "0.75rem",
                  fontWeight: "bold",
                  animation: "fadeIn 0.3s ease-in",
                  "@keyframes fadeIn": {
                    from: { opacity: 0, transform: "scale(0.8)" },
                    to: { opacity: 1, transform: "scale(1)" }
                  }
                }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "dniCuitCuil",
      headerName: "DNI/CUIT/CUIL",
      width: 150,
      flex: 0.8,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          DNI/CUIT/CUIL
        </Typography>
      ),
      renderCell: (params) => {
        const dniFormateado = formatIntegerNumber(params.value);
        return (
          <Typography variant="body2">
            {dniFormateado}
          </Typography>
        );
      },
    },
    {
      field: "itemsDescriptions",
      headerName: "Items",
      width: 200,
      flex: 1.2,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Items
        </Typography>
      ),
      renderCell: (params) => {
        const items = params.value || [];
        const itemCount = params.row.itemsCount || 0;

        if (itemCount === 0) {
          return <Typography variant="body2">Sin items</Typography>;
        }

        if (items.length === 1) {
          return (
            <Tooltip title={items[0]}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <ShoppingCartIcon
                  fontSize="small"
                  sx={{ color: alpha("#5b5b5f", 0.7) }}
                />
                <Typography variant="body2" noWrap>
                  {items[0]}
                </Typography>
              </Box>
            </Tooltip>
          );
        }

        return (
          <Tooltip title={items.join(", ")}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Badge badgeContent={itemCount} color="primary">
                <ShoppingCartIcon
                  fontSize="small"
                  sx={{ color: alpha("#5b5b5f", 0.7) }}
                />
              </Badge>
              <Typography variant="body2" noWrap>
                {items.length > 0 ? `${items[0]}, ...` : "Sin items"}
              </Typography>
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "monto",
      headerName: "Monto",
      type: "number",
      width: 150,
      flex: 0.8,
      headerAlign: "center",
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "white", fontWeight: "bold" }}
          >
            Monto
          </Typography>
          <Tooltip title="Adelantos dados (proveedores) o precio de paquetes con intereses aplicados según vencimientos (clientes)">
            <InfoIcon fontSize="small" sx={{ color: "white", opacity: 0.7 }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        const val = params.value ?? 0;
        return (
          <Typography variant="body2" fontWeight="medium" color="primary">
            {formatCurrency(val)}
          </Typography>
        );
      },
    },
    {
      field: "cobrado",
      headerName: "Cobrado",
      width: 120,
      flex: 0.8,
      headerAlign: "center",
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "white", fontWeight: "bold" }}
          >
            Cobrado
          </Typography>
          <Tooltip title="Servicios contratados (proveedores) o cuotas pagadas (clientes)">
            <InfoIcon fontSize="small" sx={{ color: "white", opacity: 0.7 }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => (
        <Chip
          label={formatCurrency(params.value || 0)}
          size="small"
          sx={{
            bgcolor: alpha(theme.palette.success.main, 0.1),
            color: theme.palette.success.main,
            fontWeight: "bold",
          }}
        />
      ),
    },
    {
      field: "saldoCalculado",
      headerName: "Saldo",
      type: "number",
      width: 150,
      flex: 0.8,
      headerAlign: "center",
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "white", fontWeight: "bold" }}
          >
            Saldo
          </Typography>
          <Tooltip title="Diferencia entre Monto y Cobrado (monto - cobrado)">
            <InfoIcon fontSize="small" sx={{ color: "white", opacity: 0.7 }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        const val = params.value ?? 0;
        let color, icon;
        if (val === 0) {
          color = theme.palette.success.main;
          icon = <BalanceIcon fontSize="small" />;
        } else if (val > 0) {
          color = theme.palette.warning.main;
          icon = <TrendingUpIcon fontSize="small" />;
        } else {
          color = theme.palette.error.main;
          icon = <TrendingDownIcon fontSize="small" />;
        }
        return (
          <Chip
            icon={icon}
            label={`$${val.toLocaleString("es-AR")}`}
            size="small"
            sx={{
              bgcolor: alpha(color, 0.1),
              color,
              fontWeight: "bold",
              "& .MuiChip-icon": { color },
            }}
          />
        );
      },
    },
    {
      field: "saldo",
      headerName: "Saldo",
      type: "number",
      width: 150,
      flex: 0.8,
      headerAlign: "center",
      renderHeader: () => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "white", fontWeight: "bold" }}
          >
            Saldo
          </Typography>
          <Tooltip title="Deudas no relacionadas a servicios asociados a paquetes">
            <InfoIcon fontSize="small" sx={{ color: "white", opacity: 0.7 }} />
          </Tooltip>
        </Box>
      ),
      renderCell: (params) => {
        const val = params.value ?? 0;
        let color, icon;
        if (val === 0) {
          color = theme.palette.success.main;
          icon = <BalanceIcon fontSize="small" />;
        } else if (val > 0) {
          color = theme.palette.warning.main;
          icon = <TrendingUpIcon fontSize="small" />;
        } else {
          color = theme.palette.error.main;
          icon = <TrendingDownIcon fontSize="small" />;
        }
        return (
          <Chip
            icon={icon}
            label={`$${val.toLocaleString("es-AR")}`}
            size="small"
            sx={{
              bgcolor: alpha(color, 0.1),
              color,
              fontWeight: "bold",
              "& .MuiChip-icon": { color },
            }}
          />
        );
      },
    },
    {
      field: "transCount",
      headerName: "# Pagos",
      width: 120,
      flex: 0.6,
      headerAlign: "center",
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          # Pagos
        </Typography>
      ),
      renderCell: (params) => (
        <Chip
          label={params.value || 0}
          size="small"
          color="info"
          variant="outlined"
        />
      ),
    },
    {
      field: "actions",
      headerName: "Acciones",
      width: 120,
      flex: 0.5,
      headerAlign: "center",
      sortable: false,
      renderHeader: () => (
        <Typography
          variant="subtitle2"
          sx={{ color: "white", fontWeight: "bold" }}
        >
          Acciones
        </Typography>
      ),
      renderCell: (params) => {
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Ver detalles">
              <IconButton
                size="small"
                onClick={() => handleOpenModal(params.row)}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Pagar">
              <IconButton
                size="small"
                onClick={() => handleOpenMediosDePago(params.row)}
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <PaymentsIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // Filtrar y modificar columnas según el tab activo
  const columnsToShow = columns
    .map(col => {
      if (tabActivo === 'adelantos') {
        // Tab "Cuenta Corriente": Modificar acciones para quitar botón "Pagar"
        if (col.field === 'actions') {
          return {
            ...col,
            renderCell: (params) => {
              return (
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Tooltip title="Ver detalles">
                    <IconButton
                      size="small"
                      onClick={() => handleOpenModal(params.row)}
                      sx={{
                        bgcolor: alpha(theme.palette.primary.main, 0.1),
                        "&:hover": {
                          bgcolor: alpha(theme.palette.primary.main, 0.2),
                        },
                      }}
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              );
            },
          };
        }
      }
      if (tabActivo === 'deudas') {
        if (col.field === 'descripcion') {
          return {
            ...col,
            headerName: "Proveedores",
            renderHeader: () => (
              <Typography
                variant="subtitle2"
                sx={{ color: "white", fontWeight: "bold" }}
              >
                Proveedores
              </Typography>
            ),
          };
        }
      }
      // Para otros casos, devolver la columna sin modificar
      return col;
    })
    .filter(col => {
      if (tabActivo === 'adelantos') {
        // Tab "Cuenta Corriente": Quitar items, fecha, saldo (legacy)
        if (col.field === 'itemsDescriptions' || col.field === 'fechaCreacionCC' || col.field === 'saldo') {
          return false;
        }
      } else if (tabActivo === 'deudas') {
        // Tab "Deudas": Quitar items, fecha, monto, cobrado, saldo (calculado) y tipo entidad
        if (col.field === 'itemsDescriptions' || col.field === 'fechaCreacionCC' || 
            col.field === 'monto' || col.field === 'cobrado' || col.field === 'saldoCalculado' ||
            col.field === 'tipoEntidad') {
          return false;
        }
      }
      return true;
    });

  // Filtrar datos según el tab activo
  const dataToShow = tabActivo === 'deudas' 
    ? filteredData.filter(row => row.tipoEntidad === 'Proveedor')
    : filteredData;

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{ p: 2 }}>
        {/* Tabs para seleccionar vista */}
        <Paper
          elevation={2}
          sx={{
            mb: 3,
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Tabs
            value={tabActivo}
            onChange={(e, newValue) => setTabActivo(newValue)}
            sx={{
              backgroundColor: alpha(theme.palette.primary.main, 0.05),
              "& .MuiTabs-indicator": {
                backgroundColor: theme.palette.secondary.main,
                height: 3,
              },
            }}
          >
            <Tab
              value="adelantos"
              label="Cuenta Corriente"
              icon={<AccountBalanceWalletIcon />}
              iconPosition="start"
              sx={{
                fontWeight: 600,
                textTransform: "none",
                fontSize: "1rem",
                minHeight: 64,
                "&.Mui-selected": {
                  color: theme.palette.secondary.main,
                },
              }}
            />
            <Tab
              value="deudas"
              label="Deudas"
              icon={<ReceiptIcon />}
              iconPosition="start"
              sx={{
                fontWeight: 600,
                textTransform: "none",
                fontSize: "1rem",
                minHeight: 64,
                "&.Mui-selected": {
                  color: theme.palette.secondary.main,
                },
              }}
            />
          </Tabs>
        </Paper>

        {/* Tarjetas de resumen */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          {tabActivo === 'adelantos' ? (
            <>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <TrendingDownIcon
                        sx={{ color: theme.palette.error.main, mr: 1 }}
                      />
                      <Typography variant="h6">Saldo a Cobrar</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Monto Total:
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="error.main"
                      >
                        {formatCurrency(stats.saldoACobrar)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">
                        Clientes:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {stats.cantidadClientesDeudores}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent sx={{ p: 2 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <TrendingUpIcon
                        sx={{ color: theme.palette.success.main, mr: 1 }}
                      />
                      <Typography variant="h6">Saldo a Favor</Typography>
                    </Box>
                    <Divider sx={{ mb: 2 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        mb: 1,
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        Monto Total:
                      </Typography>
                      <Typography
                        variant="body1"
                        fontWeight="bold"
                        color="success.main"
                      >
                        {formatCurrency(stats.saldoAFavor)}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography variant="body2" color="text.secondary">
                        Proveedores:
                      </Typography>
                      <Typography variant="body1" fontWeight="bold">
                        {stats.cantidadProveedoresCredito}
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </>
          ) : (
            <Grid item xs={12} md={4}>
              <Card>
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <TrendingUpIcon
                      sx={{ color: theme.palette.warning.main, mr: 1 }}
                    />
                    <Typography variant="h6">Cuentas por Pagar</Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Monto Total:
                    </Typography>
                    <Typography
                      variant="body1"
                      fontWeight="bold"
                      color="warning.main"
                    >
                      {formatCurrency(stats.totalDeudas)}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      mb: 1,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Cantidad:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {stats.cantidadDeudas} deudas
                    </Typography>
                  </Box>
                  <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Typography variant="body2" color="text.secondary">
                      Promedio:
                    </Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {stats.cantidadDeudas > 0
                        ? formatCurrency(stats.totalDeudas / stats.cantidadDeudas)
                        : formatCurrency(0)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Filtros */}
        <Paper
          className="diseñoFiltroTransaccionesVenta"
          elevation={3}
          sx={{
            p: 2,
            mb: 2,
            borderRadius: 2,
            backgroundColor: alpha(theme.palette.background.paper, 0.9),
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 2,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {tabActivo === "adelantos" && (
              <Autocomplete
                size="small"
                options={tiposEntidad}
                value={tipoEntidadFilter}
                onChange={(event, newValue) => setTipoEntidadFilter(newValue)}
                sx={{ minWidth: 200, flex: "1 1 200px" }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tipo de Entidad"
                    size="small"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
              />
            )}

            <TextField
              label="Entidad"
              size="small"
              value={searchEntidadText}
              onChange={(event) => setSearchEntidadText(event.target.value)}
              sx={{
                minWidth: 220,
                flex: "1 1 220px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              label="DNI/CUIT/CUIL"
              size="small"
              value={searchDniText}
              onChange={(event) => setSearchDniText(event.target.value)}
              sx={{
                minWidth: 200,
                flex: "1 1 200px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <TextField
              label="# Pagos"
              type="number"
              size="small"
              value={searchPagosText}
              onChange={(event) => setSearchPagosText(event.target.value)}
              sx={{
                minWidth: 120,
                flex: "1 1 120px",
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                },
              }}
            />

            <Button
              variant="contained"
              size="medium"
              onClick={buttonFiltrado}
              startIcon={<SearchIcon />}
              sx={{
                backgroundColor: theme.palette.primary.main,
                textTransform: "none",
                padding: "10px 20px",
                borderRadius: 2,
                fontWeight: 600,
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                "&:hover": {
                  backgroundColor: theme.palette.secondary.main,
                  transform: "translateY(-1px)",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                },
                transition: "all 0.2s ease",
              }}
            >
              Buscar
            </Button>

            <Tooltip title="Limpiar filtros">
              <IconButton
                onClick={clearFilters}
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  "&:hover": {
                    bgcolor: alpha(theme.palette.error.main, 0.2),
                  },
                }}
              >
                <FilterAltOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Paper>

        {/* Tabla */}
        <Paper sx={{ height: 500, width: "100%" }}>
          <DataGrid
            key={isNavbarCollapsed ? "collapsed" : "expanded"}
            rows={
              dataToShow.length > 0
                ? dataToShow
                : cuentasCorrientesTransacciones
            }
            columns={columnsToShow}
            getRowId={(row) => row.idEntidad}
            pageSize={10}
            rowsPerPageOptions={[10, 25, 50]}
            disableSelectionOnClick
            className="tablaVentasDataGrid"
            sx={{
              width: "100%",
              border: 0,
              "& .MuiDataGrid-footerContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaders": {
                backgroundColor: "#5b5b5f",
                color: "white",
                borderBottom: "2px solid #333",
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeader": {
                height: "45px !important",
                minHeight: "45px !important",
                maxHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                color: "white !important",
                lineHeight: "45px !important",
              },
              "& .MuiDataGrid-columnHeaderTitle .MuiDataGrid-menuIconButton": {
                color: "white !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiInputBase-root": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiTablePagination-root": {
                color: "black !important",
              },
              "& .MuiSelect-icon": {
                color: "rgb(244, 177, 14) !important",
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #ccc",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              },
              "& .MuiDataGrid-row": {
                "&:nth-of-type(even)": {
                  backgroundColor: "#F7F7F7",
                },
                "&:hover": {
                  backgroundColor: "#E3EAFD !important",
                  transition: "background 0.3s ease",
                },
              },
              "& .row-devolucion": {
                backgroundColor: alpha("#9e9e9e !important", 0.05),
                "&:hover": {
                  backgroundColor: alpha("#9e9e9e !important", 0.1),
                },
              },
              "& .MuiDataGrid-virtualScroller": {
                lineHeight: "none !important",
                height: "10px !imporant",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
                width: "2px",
              },
              "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
            }}
            getRowClassName={(params) => {
              if (params.row.saldo === 0) return "row-saldado";
              if (params.row.saldo > 0) return "row-deuda";
              return "row-deudor";
            }}
            loading={loading}
          />
        </Paper>

        {/* Contenedor del footer (paginación) */}
        <Box sx={{ flexShrink: 0, p: 1 }}>
          <CustomPagination
            currentPage={page}
            totalPages={totalPages}
            totalCount={totalCount}
            montoTotalTransacciones={montoTotalTransacciones}
            vieneDe={"cuentaCorriente"}
            onPageChange={(newPage) => {
              setPage(newPage);
              fetchData(newPage);
            }}
          />
        </Box>

        {/* Modal de detalles */}
        <ModalDetalleCuentaCorriente
          open={openModal}
          handleClose={handleCloseModal}
          cuentaCorriente={selectedCuentaCorriente}
          tabActivo={tabActivo}
        />

        <ModalMedioDePago
          open={abrirMedioDePago}
          handleClose={handleCloseMedioDePago}
          precioTotal={precioTotal}
          transaccionSeleccionada={selectedCuentaCorriente}
          onConfirmarPago={handleConfirmarPago}
          filtrarPorCrearEnCuentaCorriente={tabActivo === 'deudas'}
        />
      </Box>
    </ThemeProvider>
  );
};

export default CuentasCorrientesTable;
